import { cn } from '@/lib/cn';
import { useLocalePrefs } from '@/contexts/LocaleContext';
import { dateInputPlaceholder, isoToBr, maskDateInput, parseMaskedDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import type { UiLocale } from '@/lib/locale';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { es } from 'date-fns/locale/es';
import { ptBR } from 'date-fns/locale/pt-BR';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Locale } from 'date-fns';

type DateInputProps = {
  label?: string;
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  id?: string;
};

function dateFnsLocale(ui: UiLocale): Locale {
  if (ui === 'es') return es;
  if (ui === 'en-US') return enUS;
  return ptBR;
}

function parseIsoDay(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toIsoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function DateInput({
  label,
  value,
  onChange,
  required,
  error,
  hint,
  className,
  id,
}: DateInputProps) {
  const t = useT();
  const prefs = useLocalePrefs();
  const autoId = useId();
  const inputId = id ?? autoId;
  const rootRef = useRef<HTMLDivElement>(null);
  const dfLocale = dateFnsLocale(prefs.uiLocale);
  const [text, setText] = useState(() => isoToBr(value));
  const [localError, setLocalError] = useState('');
  const [open, setOpen] = useState(false);
  const selected = parseIsoDay(value);
  const [viewMonth, setViewMonth] = useState(() => selected ?? new Date());

  useEffect(() => {
    setText(isoToBr(value));
    setLocalError('');
    const next = parseIsoDay(value);
    if (next) setViewMonth(next);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleTyped(raw: string) {
    const masked = maskDateInput(raw, prefs.dateFormat);
    setText(masked);
    if (masked === '') {
      setLocalError('');
      onChange('');
      return;
    }
    const digits = masked.replace(/\D/g, '');
    if (digits.length < 8) {
      setLocalError('');
      return;
    }
    const iso = parseMaskedDate(masked, prefs.dateFormat);
    if (!iso) {
      setLocalError('Data inválida.');
      return;
    }
    setLocalError('');
    onChange(iso);
  }

  const shownError = error || localError;
  const weekStartsOn = (dfLocale.options?.weekStartsOn ?? 0) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { locale: dfLocale, weekStartsOn });
    const end = endOfWeek(endOfMonth(viewMonth), { locale: dfLocale, weekStartsOn });
    const days = eachDayOfInterval({ start, end });
    const rows: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return rows;
  }, [viewMonth, dfLocale, weekStartsOn]);

  const weekdayLabels = weeks[0]?.map((day) =>
    format(day, 'EEEEE', { locale: dfLocale }),
  ) ?? [];

  function pickDay(day: Date) {
    onChange(toIsoDay(day));
    setOpen(false);
  }

  return (
    <div className={cn('space-y-1.5', className)} ref={rootRef}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-manejai-bark">
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          inputMode="numeric"
          autoComplete="off"
          required={required}
          value={text}
          placeholder={dateInputPlaceholder(prefs.dateFormat)}
          onChange={(e) => handleTyped(e.target.value)}
          className={cn(
            'w-full h-10 pl-3 pr-10 rounded-xl border bg-white text-sm transition-colors',
            'placeholder:text-manejai-bark/40',
            'focus:outline-none focus:ring-2 focus:ring-manejai-sage/40 focus:border-manejai-sage',
            shownError ? 'border-red-400' : 'border-manejai-sand',
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            setViewMonth(selected ?? new Date());
            setOpen((v) => !v);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-manejai-bark/50 hover:bg-manejai-cream hover:text-manejai-forest"
          aria-label={t('calendar.open')}
          aria-expanded={open}
        >
          <CalendarDays className="h-4 w-4" />
        </button>

        {open && (
          <div className="absolute right-0 z-50 mt-2 w-[18.5rem] rounded-2xl border border-manejai-sand bg-white p-3 shadow-elevated">
            <div className="mb-2 flex items-center gap-1">
              <NavButton
                label={t('calendar.prev_year')}
                onClick={() => setViewMonth((d) => addYears(d, -1))}
              >
                <ChevronsLeft className="h-4 w-4" />
              </NavButton>
              <NavButton
                label={t('calendar.prev_month')}
                onClick={() => setViewMonth((d) => addMonths(d, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </NavButton>
              <p className="min-w-0 flex-1 text-center text-sm font-semibold capitalize text-manejai-forest">
                {capitalize(format(viewMonth, 'LLLL yyyy', { locale: dfLocale }))}
              </p>
              <NavButton
                label={t('calendar.next_month')}
                onClick={() => setViewMonth((d) => addMonths(d, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </NavButton>
              <NavButton
                label={t('calendar.next_year')}
                onClick={() => setViewMonth((d) => addYears(d, 1))}
              >
                <ChevronsRight className="h-4 w-4" />
              </NavButton>
            </div>

            <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] font-medium uppercase tracking-wide text-manejai-bark/50">
              {weekdayLabels.map((label, i) => (
                <span key={`${label}-${i}`} className="py-1">
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-1 grid grid-cols-7 gap-0.5">
              {weeks.flat().map((day) => {
                const inMonth = isSameMonth(day, viewMonth);
                const selectedDay = selected ? isSameDay(day, selected) : false;
                const today = isToday(day);
                return (
                  <button
                    key={toIsoDay(day)}
                    type="button"
                    onClick={() => pickDay(day)}
                    className={cn(
                      'h-9 rounded-full text-sm transition-colors',
                      inMonth ? 'text-manejai-bark' : 'text-manejai-bark/30',
                      !selectedDay && inMonth && 'hover:bg-manejai-cream',
                      today && !selectedDay && 'ring-1 ring-inset ring-manejai-sage',
                      selectedDay && 'bg-manejai-forest font-semibold text-white hover:bg-manejai-moss',
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-manejai-sand/70 pt-2">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="rounded-lg px-2 py-1 text-xs font-medium text-manejai-bark/70 hover:bg-manejai-cream hover:text-manejai-forest"
              >
                {t('calendar.clear')}
              </button>
              <button
                type="button"
                onClick={() => pickDay(new Date())}
                className="rounded-lg px-2 py-1 text-xs font-medium text-manejai-forest hover:bg-manejai-cream"
              >
                {t('calendar.today')}
              </button>
            </div>
          </div>
        )}
      </div>
      {shownError && <p className="text-xs text-red-600">{shownError}</p>}
      {hint && !shownError && <p className="text-xs text-manejai-bark/60">{hint}</p>}
    </div>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-lg p-1 text-manejai-bark/55 hover:bg-manejai-cream hover:text-manejai-forest"
    >
      {children}
    </button>
  );
}
